import React, { useState, useRef, useEffect } from 'react';
import './Pricesortdropdown.css';

/**
 * Price Sort Dropdown Component with 3 options
 * Compatible with existing toggle/clear callback pattern
 * 
 * @param {string|null} sortOrder - Current sort order: null, 'asc', or 'desc'
 * @param {function} onToggleSort - Callback to toggle sort (null -> asc -> desc -> asc)
 * @param {function} onClearSort - Callback to clear sort
 */
const PriceSortDropdown = ({ sortOrder, onToggleSort, onClearSort }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get display text based on current sort order
  const getDisplayText = () => {
    if (sortOrder === 'asc') return 'Low to High';
    if (sortOrder === 'desc') return 'High to Low';
    return 'Transfer Price';
  };

  // Handle option selection - works with toggle pattern
  const handleSelect = (option) => {
    setIsOpen(false);
    
    if (option === 'clear') {
      onClearSort();
      return;
    }
    
    // If selecting Low to High (asc)
    if (option === 'asc') {
      if (sortOrder === null) {
        // null -> asc (one toggle)
        onToggleSort();
      } else if (sortOrder === 'desc') {
        // desc -> asc (one toggle)
        onToggleSort();
      }
      // If already asc, do nothing
      return;
    }
    
    // If selecting High to Low (desc)
    if (option === 'desc') {
      if (sortOrder === null) {
        // null -> asc -> desc (two toggles)
        onToggleSort();
        // Use setTimeout to ensure state updates
        setTimeout(() => onToggleSort(), 10);
      } else if (sortOrder === 'asc') {
        // asc -> desc (one toggle)
        onToggleSort();
      }
      // If already desc, do nothing
    }
  };

  // Toggle dropdown with debugging
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Toggle clicked, current isOpen:', isOpen);
    setIsOpen(!isOpen);
  };

  // Debug: Log when isOpen changes
  useEffect(() => {
    console.log('Dropdown isOpen changed to:', isOpen);
  }, [isOpen]);

  return (
    <div className="price-sort-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`price-sort-button ${sortOrder ? 'active' : ''} ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        title={sortOrder ? `Current: ${getDisplayText()}` : 'Sort by Transfer Price'}
      >
        <span className="sort-button-text">{getDisplayText()}</span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path 
            d="M2.5 4.5L6 8L9.5 4.5" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="dropdown-menu" style={{ display: 'block' }}>
          <button
            type="button"
            className={`dropdown-item ${sortOrder === 'asc' ? 'selected' : ''}`}
            onClick={() => handleSelect('asc')}
            title="Sort from lowest to highest price"
          >
            Low to High
            {sortOrder === 'asc' && <span className="checkmark">✓</span>}
          </button>
          <button
            type="button"
            className={`dropdown-item ${sortOrder === 'desc' ? 'selected' : ''}`}
            onClick={() => handleSelect('desc')}
            title="Sort from highest to lowest price"
          >
            High to Low
            {sortOrder === 'desc' && <span className="checkmark">✓</span>}
          </button>
          <div className="dropdown-divider"></div>
          <button
            type="button"
            className="dropdown-item clear-option"
            onClick={() => handleSelect('clear')}
            title="Remove sort and return to default order"
          >
            Clear Sort
          </button>
        </div>
      )}
    </div>
  );
};

export default PriceSortDropdown;